import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface CurrencySettings {
  currency: string;
  currency_locale: string;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: "€",
  GBP: "£",
  USD: "$",
  MAD: "د.م.",
};

export function useCurrencySettings() {
  const { company } = useAuth();

  const { data: settings, isLoading } = useQuery({
    queryKey: ["currency-settings", company?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("company_settings")
        .select("currency, currency_locale")
        .eq("company_id", company!.id)
        .single();

      if (error) {
        console.error("Error fetching currency settings:", error);
        return { currency: "EUR", currency_locale: "es-ES" };
      }

      return data as CurrencySettings;
    },
    enabled: !!company?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const currency = settings?.currency || "EUR";
  const locale = settings?.currency_locale || "es-ES";
  const symbol = CURRENCY_SYMBOLS[currency] || "€";

  const formatCurrency = (
    amount: number | null | undefined,
    opts?: { decimals?: number },
  ): string => {
    const digits = opts?.decimals ?? 2;
    if (amount === null || amount === undefined) return `${symbol}${(0).toFixed(digits)}`;

    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency,
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(amount);
    } catch {
      // Fallback if Intl fails
      return `${symbol}${amount.toFixed(digits)}`;
    }
  };

  const formatNumber = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined) return "0.00";
    
    try {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return amount.toFixed(2);
    }
  };

  return {
    currency,
    locale,
    symbol,
    formatCurrency,
    formatNumber,
    isLoading,
  };
}

// Export constants for use in other places
export const SUPPORTED_CURRENCIES = [
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "MAD", symbol: "د.م.", name: "Moroccan Dirham" },
];

export const SUPPORTED_LOCALES = [
  { code: "es-ES", name: "Spanish (1.234,56)" },
  { code: "en-GB", name: "British (1,234.56)" },
  { code: "en-US", name: "US (1,234.56)" },
  { code: "ar-MA", name: "Moroccan (1.234,56)" },
];
